/**
 * Correcting a game that was entered wrongly — FR-ENTRY-10, ENTRY-20..25.
 *
 * Until now the only correction available anywhere in the app was Undo, and
 * only on the most recent game. Anything older — a score typed as 15-9 when it
 * was 15-6, the wrong side marked as winner — was permanent as far as the club
 * could tell. `PATCH /api/games/:id` and the whole classified-audit edit
 * service have existed since task 1.10 with nothing calling them.
 *
 * The same two decisions as entry, in the same order, because it is the same
 * question asked again: **who won**, then **what did the other side get**.
 * Somebody correcting a game already knows both, and a form with four number
 * fields would invite a new mistake while fixing an old one.
 *
 * FR-ENTRY-8 holds here too: the grid offers only legal results, so a
 * correction cannot introduce an impossible score. The options come from the
 * session view, which the server built from `scoring-rules`.
 */

import { useState } from "react";
import { api, type Game, type ScoreOption } from "../api.js";

export function EditGame({
  game,
  nameOf,
  targets,
  optionsByTarget,
  onCancel,
  onSaved,
}: {
  game: Game;
  nameOf: (id: string) => string;
  targets: number[];
  optionsByTarget: Record<string, ScoreOption[]>;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [winner, setWinner] = useState<"A" | "B">(game.winnerSide);
  const [target, setTarget] = useState(game.targetScore);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = optionsByTarget[String(target)] ?? [];
  const normal = options.filter((o) => o.group === "normal" && o.winnerScore === target);
  const deuce = options.filter((o) => o.group === "deuce");

  const sideA = game.sideA.map(nameOf).join(" & ");
  const sideB = game.sideB.map(nameOf).join(" & ");

  async function save(winnerScore: number, loserScore: number): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const [sideAScore, sideBScore] =
        winner === "A" ? [winnerScore, loserScore] : [loserScore, winnerScore];
      await api.editGame(game.id, {
        sideAScore,
        sideBScore,
        targetScore: target,
        reason: "Corrected after entry",
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save that correction.");
      setBusy(false);
    }
  }

  return (
    <section className="block">
      <h3 className="card-head">
        Correct this game
        <button className="mini" onClick={onCancel}>
          Cancel
        </button>
      </h3>

      <p className="muted small">
        Was {sideA} {game.sideAScore}–{game.sideBScore} {sideB}. Every change is
        recorded against the session.
      </p>

      {/* Decision one, exactly as at entry. */}
      <div className="row" style={{ margin: ".6rem 0 .4rem" }}>
        <span className="muted small">Winner</span>
        {(["A", "B"] as const).map((side) => (
          <button
            key={side}
            className={side === winner ? "chip chip-on" : "chip"}
            onClick={() => setWinner(side)}
          >
            {side === "A" ? sideA : sideB}
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: ".5rem" }}>
        <span className="muted small">to</span>
        {targets.map((t) => (
          <button
            key={t}
            className={t === target ? "chip chip-on" : "chip"}
            onClick={() => setTarget(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {error ? <p className="error">{error}</p> : null}

      {/* Decision two. Only legal values are ever on the screen. */}
      <p className="gridlbl">Won {target} — tap what the other side got</p>
      <div className="grid">
        {normal.map((o) => (
          <button
            key={`n-${String(o.loserScore)}`}
            className="score-cell num"
            disabled={busy}
            onClick={() => {
              void save(o.winnerScore, o.loserScore);
            }}
          >
            {o.loserScore}
          </button>
        ))}
      </div>

      {deuce.length > 0 ? (
        <>
          <p className="gridlbl">Or it went past {target}</p>
          <div className="grid grid-wide">
            {deuce.map((o) => (
              <button
                key={`d-${String(o.winnerScore)}-${String(o.loserScore)}`}
                className="score-cell num"
                disabled={busy}
                onClick={() => {
                  void save(o.winnerScore, o.loserScore);
                }}
              >
                {o.winnerScore}–{o.loserScore}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
